/**
 * İSTEMCİ DEPOLAMA SÖZLEŞMESİ — panel eşleniği (web'deki sınıfın aynısı).
 *
 * Tarayıcıda KALICI yazılan her anahtar buradan geçer. İki kova vardır:
 * - **Kullanıcıya özel** (`USER_SCOPED_PREFIXES`): oturum sınırında TEK yerden
 *   silinir (`clearUserScopedStorage`).
 * - **Bilinçli korunan** (`PRESERVED_KEYS`): cihaz/tarayıcı tercihi — kullanıcı
 *   verisi değildir, hesap değişse de kalır.
 *
 * Kayıtlı olmayan bir anahtarla yazmak HATA fırlatır; `client-storage.spec.ts`
 * doğrudan `localStorage` kullanan yeni dosyayı yakalar ve eslint kuralı
 * (`no-direct-client-storage`) sürücüye erişimi lint düzeyinde kapatır. Üç
 * ayak birlikte: yeni anahtar temizlik listesine girmeden merge edilemez.
 */

/**
 * Kullanıcıya ait, oturum sınırında SİLİNEN önekler.
 *
 * - `wb_panel_write_key:`: sonucu bilinmeyen yazmanın yineleme anahtarı
 *   (C17). Sayfa yenilense de aynı gövde aynı anahtarla gider ve sunucu ikinci
 *   kredi satırını yazmaz; çıkışta silinir, başka personele geçmez.
 */
export const WRITE_KEY_STORAGE_PREFIX = 'wb_panel_write_key:';
export const USER_SCOPED_PREFIXES: readonly string[] = [WRITE_KEY_STORAGE_PREFIX];

/**
 * Bilinçli KORUNANLAR — kullanıcı verisi değil, tarayıcı tercihi:
 * - `wb_theme`: açık/karanlık tercihi,
 * - `wb_locale`: dil tercihi (web'de çerezde yaşar; panelde SSR yok,
 *   bu yüzden aynı ad `localStorage`ta tutulur — anahtar adı ortak sözleşmeden
 *   gelir: `LOCALE_STORAGE_KEY`).
 */
export const PRESERVED_KEYS = ['wb_theme', 'wb_locale'] as const;

function isPreserved(key: string): boolean {
  return (PRESERVED_KEYS as readonly string[]).includes(key);
}

function isUserScoped(key: string): boolean {
  return USER_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Gizli sekme / depolama kapalı: panel depolamasız da çalışır.
    return null;
  }
}

export function readPreserved(key: string): string | null {
  if (!isPreserved(key)) throw new Error(`unregistered preserved key: ${key} (PRESERVED_KEYS)`);
  return store()?.getItem(key) ?? null;
}

export function writePreserved(key: string, value: string): void {
  if (!isPreserved(key)) throw new Error(`unregistered preserved key: ${key} (PRESERVED_KEYS)`);
  try {
    store()?.setItem(key, value);
  } catch {
    // Kota dolu / yazma reddedildi: tercih kalıcı olmaz, uygulama düşmez.
  }
}

export function readUserScoped(key: string): string | null {
  if (!isUserScoped(key)) throw new Error(`unregistered key: ${key} (USER_SCOPED_PREFIXES)`);
  return store()?.getItem(key) ?? null;
}

export function writeUserScoped(key: string, value: string): void {
  if (!isUserScoped(key)) throw new Error(`unregistered key: ${key} (USER_SCOPED_PREFIXES)`);
  try {
    store()?.setItem(key, value);
  } catch {
    // Aynı gerekçe: kalıcılık en iyi çabadır, ana iş düşmez.
  }
}

export function removeUserScoped(key: string): void {
  if (!isUserScoped(key)) throw new Error(`unregistered key: ${key} (USER_SCOPED_PREFIXES)`);
  try {
    store()?.removeItem(key);
  } catch {
    // Depolama kapalı: silinecek bir şey de yazılamamıştır.
  }
}

/** Oturum sınırı: kullanıcıya ait TÜM anahtarlar silinir, korunanlara dokunulmaz. */
export function clearUserScopedStorage(): void {
  const storage = store();
  if (!storage) return;
  const doomed: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && !isPreserved(key) && isUserScoped(key)) doomed.push(key);
  }
  for (const key of doomed) storage.removeItem(key);
}
